import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
// currency helpers
import { useCurrencyUtils } from "../../utils/currencyUtils";
import api from "../../lib/api.ts";
import Pagination from "../common/Pagination";
import { useAuth } from "../../context/AuthContext";
import SellerInvoicePaymentPopup from "../common/SellerInvoicePaymentPopup";
import { CreditCard, FileText } from "lucide-react";
import { generateSellerInvoicePdf } from "../../utils/sellerInvoicePdf";

const SellerInvoices = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailsInvoice, setSelectedDetailsInvoice] = useState(null);

  const { user } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchSellerInvoices = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          page: currentPage,
          limit: pageSize,
          orderBy: "id",
          order: "desc",
          sellerId: user.seller.id,
          filters: {},
        };

        const response = await api.get(`/sellerinvoices/paginated`, { params });

        const data = response?.data?.data || response?.data || [];
        setInvoices(Array.isArray(data) ? data : []);

        // Update pagination state from API response
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.page,
            totalPages: response.data.pagination.totalPages,
            totalItems: response.data.pagination.totalItems,
            itemsPerPage: pageSize,
          });
        } else {
          console.log("No pagination data in API response");
        }
      } catch (err) {
        setError(translate("sellerInvoices.messages.failedToLoad"));
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerInvoices();
  }, [currentPage, pageSize]);

  // Payment popup functions
  const handlePayNow = (invoice) => {
    setSelectedPaymentInvoice(invoice);
    setShowPaymentPopup(true);
  };

  const handleClosePaymentPopup = () => {
    setShowPaymentPopup(false);
    setSelectedPaymentInvoice(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    window.location.reload();
  };

  // Determine action state for an invoice
  const getActionState = (inv) => {
    const payments = inv?.sellerInvoicePayments || [];
    const isPaid =
      inv?.invoicePaymentStatus?.slug === "paid" ||
      inv?.invoicePaymentStatus?.name === "Paid" ||
      inv?.invoicePaymentStatusId === 3;
    if (isPaid) return "paid";
    const hasPending = payments.some((p) => p.isApproved === null);
    if (hasPending) return "pending-approval";
    const hasRejected = payments.some((p) => p.isApproved === false || p.isApproved === 0);
    if (hasRejected) return "rejected";
    return "unpaid";
  };

  // Get latest rejection note
  const getLatestRejectionNote = (inv) => {
    const payments = inv?.sellerInvoicePayments || [];
    const rejected = payments
      .filter((p) => p.isApproved === false || p.isApproved === 0)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return rejected[0]?.rejectionNote || null;
  };

  // Payment details modal component
  const PaymentDetailsModal = ({ invoice, onClose }) => {
    if (!invoice) return null;
    const payments = invoice?.sellerInvoicePayments || [];
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Payment History</h3>
              <p className="text-xs text-gray-500">Invoice #{invoice.id} — {invoice?.vehicle?.name || "-"}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-4 space-y-3">
            {payments.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">No payment submissions yet</p>
            ) : (
              payments
                .slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((payment, idx) => {
                  const isApproved = payment.isApproved === true || payment.isApproved === 1;
                  const isRejected = payment.isApproved === false || payment.isApproved === 0;
                  return (
                    <div
                      key={payment.id || idx}
                      className={`border rounded-lg p-3 ${
                        isApproved ? "border-green-200 bg-green-50" :
                        isRejected ? "border-red-200 bg-red-50" :
                        "border-yellow-200 bg-yellow-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">
                          Submission #{payments.length - idx}
                        </span>
                        <div className="flex items-center gap-2">
                          {payment.imageUrl && (
                            <a href={payment.imageUrl} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline">View Receipt</a>
                          )}
                          {isApproved && <span className="text-xs text-green-700 font-medium">✓ Approved</span>}
                          {isRejected && <span className="text-xs text-red-700 font-medium">✗ Rejected</span>}
                          {!isApproved && !isRejected && <span className="text-xs text-yellow-700 font-medium">⏳ Pending</span>}
                        </div>
                      </div>
                      {isRejected && payment.rejectionNote && (
                        <div className="mt-2 text-xs text-red-700 bg-red-100 rounded p-2">
                          <span className="font-semibold">Rejection Reason: </span>{payment.rejectionNote}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </div>
          <div className="px-4 pb-4 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getStatusText = (status) => {
    if (!status) return "-";
    const statusMap = {
      Paid: translate("sellerInvoices.status.paid"),
      Pending: translate("sellerInvoices.status.pending"),
      "Partial Payment": translate("sellerInvoices.status.partialPayment"),
      Cancelled: translate("sellerInvoices.status.cancelled"),
      Refunded: translate("sellerInvoices.status.refunded"),
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    );
  };

  return (
    <div
      className='bg-card rounded-lg shadow '
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >

      <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]'>
        {/* Standardized container with fixed height and Gate Pass padding */}
        <div className='flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft min-h-[120px] lg:min-h-[72px]'>

          <div className='flex flex-col justify-center'>
            <h2 className='text-white text-base sm:text-lg font-semibold whitespace-nowrap'>
              {translate("sellerInvoices.heading")}
            </h2>
          </div>

          {/* Right side spacer to maintain the footprint of the search bar pages */}
          <div className='hidden lg:block'></div>

        </div>
      </div>

      {loading ? (
        <div className='p-6 text-sm text-description'>
          {translate("sellerInvoices.messages.loading")}
        </div>
      ) : error ? (
        <div className='p-6 text-sm text-red-600'>{error}</div>
      ) : invoices.length === 0 ? (
        <div className='p-6 text-sm text-center text-description'>
          {translate("sellerInvoices.messages.noInvoicesFound")}
        </div>
      ) : (
        <div className="px-3 py-6">
                <div className='overflow-x-auto rounded-lg hide-scrollbar-local'>
                  <style>{`
                    .hide-scrollbar-local::-webkit-scrollbar {
                      display: none;
                    }
                    .hide-scrollbar-local {
                      -ms-overflow-style: none;
                      scrollbar-width: none;
                    }
                  `}</style>
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className="bg-page border-b border-default py-4">
                      <tr>
                        <th
                          className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-description uppercase tracking-wider`}
                        >
                          {translate("sellerInvoices.tableHeaders.vehicle")}
                        </th>

                        <th
                          className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-description uppercase tracking-wider`}
                        >
                          {translate("sellerInvoices.tableHeaders.totalAmount")}
                        </th>

                        <th
                          className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-description uppercase tracking-wider`}
                        >
                          {translate("sellerInvoices.tableHeaders.date")}
                        </th>

                        <th
                          className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-description uppercase tracking-wider`}
                        >
                          {translate("sellerInvoices.tableHeaders.status")}
                        </th>

                        <th
                          className={`px-4 py-4 ${isRTL ? "text-right" : "text-left"
                            } text-xs font-medium text-description uppercase tracking-wider`}
                        >
                          {translate("sellerInvoices.tableHeaders.action")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100'>
                      {invoices.map((inv) => {
                        const vehicleName = inv?.vehicle?.name ?? "-";
                        const date = inv?.createdAt ?? "-";
                        const amount = inv?.totalAmount ?? 0;
                        const status = inv?.invoicePaymentStatus?.name ?? "-";
                        const actionState = getActionState(inv);
                        const rejectionNote = getLatestRejectionNote(inv);
                        const hasPayments = (inv?.sellerInvoicePayments?.length || 0) > 0;

                        return (
                          <tr key={inv?.id} className="border-b border-default hover:bg-page/60 transition-colors">
                            <td className='px-4 py-5'>
                              <div className="text-sm font-medium text-white">
                                {vehicleName}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                Lot: <span className="font-semibold text-white/80">{inv?.vehicle?.lotNumber || "-"}</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                VIN: {inv?.vehicle?.vinNumber || "-"}
                              </div>
                            </td>

                            <td className='px-4 py-5 text-sm font-medium text-description'>
                              {displayPriceWithSymbol(convertPrice(amount))}
                            </td>

                            <td className='px-4 py-5 text-sm text-description'>
                              {formatDate(date)}
                            </td>

                            <td className='px-4 py-5 text-xs'>
                              <div className="flex flex-col gap-1">
                                <span
                                  className={`self-start inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${actionState === "paid"
                                      ? "bg-green-700 text-white"
                                      : actionState === "pending-approval"
                                        ? "bg-amber-600 text-white"
                                        : actionState === "rejected"
                                          ? "bg-red-700 text-white"
                                          : "bg-yellow-700 text-white"
                                  }`}
                                >
                                  {actionState === "paid"
                                    ? translate("sellerInvoices.status.paid")
                                    : actionState === "pending-approval"
                                      ? translate("sellerInvoices.buttons.pendingApproval")
                                      : actionState === "rejected"
                                        ? "Payment Rejected"
                                        : getStatusText(status)}
                                </span>
                                {actionState === "rejected" && rejectionNote && (
                                  <span
                                    className="text-xs text-red-400 max-w-[160px] truncate cursor-pointer hover:text-red-300"
                                    title={rejectionNote}
                                    onClick={() => {
                                      setSelectedDetailsInvoice(inv);
                                      setShowDetailsModal(true);
                                    }}
                                  >
                                    {rejectionNote}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className='px-4 py-5 text-sm'>
                              <div className="flex items-center gap-2">
                                {/* View Details button */}
                                {(hasPayments || actionState === "paid") && (
                                  <button
                                    onClick={() => {
                                      setSelectedDetailsInvoice(inv);
                                      setShowDetailsModal(true);
                                    }}
                                    className="h-9 w-9 flex items-center justify-center text-blue-400 bg-blue-900/30 border border-blue-700/40 rounded-lg hover:bg-blue-800/40 transition-colors"
                                    title="View Payment Details"
                                  >
                                    <FileText size={16} />
                                  </button>
                                )}
                                
                                {/* Pay Now / Pay Again button */}
                                {(actionState === "unpaid" || actionState === "rejected") && (
                                  <button
                                    onClick={() => handlePayNow(inv)}
                                    className="h-9 w-9 flex items-center justify-center text-white bg-yellow-700 border border-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors"
                                    title={actionState === "rejected" ? "Pay Again" : "Pay Now"}
                                  >
                                    <CreditCard size={16} />
                                  </button>
                                )}
                                {/* Pending Approval: show icon only, not clickable */}
                                {actionState === "pending-approval" && (
                                  <button
                                    disabled
                                    className="h-9 w-9 flex items-center justify-center cursor-not-allowed text-white bg-amber-600 border border-amber-500 rounded-lg opacity-70"
                                    title={translate("sellerInvoices.buttons.pendingApproval")}
                                  >
                                    <CreditCard size={16} />
                                  </button>
                                )}
            
                                {/* PDF Download button - always shown */}
                                <button
                                  onClick={() => generateSellerInvoicePdf(inv, "download")}
                                  className="h-9 w-9 flex items-center justify-center text-white bg-red-600 border border-red-500 rounded-lg hover:bg-red-700 transition-colors"
                                  title="Download PDF"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                  </svg>
                                </button>
                              </div>
                                </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
            {pagination.totalPages > 1 && (
              <div className='px-6 py-4 border-t border-gray-200'>
                <Pagination
                  totalPages={pagination.totalPages}
                  currentPage={pagination.currentPage}
                  setCurrentPage={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {/* Payment Popup */}
      <SellerInvoicePaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePaymentPopup}
        selectedItem={selectedPaymentInvoice}
        itemType='invoice'
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Payment Details Modal */}
      {showDetailsModal && (
        <PaymentDetailsModal
          invoice={selectedDetailsInvoice}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedDetailsInvoice(null);
          }}
        />
      )}
    </div>
  );
};

export default SellerInvoices;